import { CircularProgress, Grid } from '@material-ui/core';
import { Button, Icons, Typography } from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext } from 'react-beautiful-dnd';

import { useMst } from '../../mobx-models/Root';
import {
  AimsFile,
  InspectionFilesEdited,
  InspectionFilesGrouped,
  InspectionFilesGroupedBasedOnExtension,
} from '../../models/models';
import type { InspectionFiles as InspectionFilesType } from '../../rxdb/inspectionFiles/inspectionFiles.types';
import { useInspectionFiles } from '../../rxdb/inspectionFiles/useInspectionFiles';
import useSelectedTask from '../../rxdb/taskList/useSelectedTask';
import { useTaskList } from '../../rxdb/taskList/useTaskList';
import { isJestTest } from '../../test-helpers/isJestTest';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useNotification } from '../../utilities/useNotification';
import { SPECIAL_CHARACTER_ERROR, TEXT } from '../../utilities/constants';
import { TaskStatus } from '../../utilities/enums';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import {
  autoVersionIfFileExists,
  getValueFromLocalStorage,
  groupArrayByKey,
  groupObjectArrayByKey,
  hasSpecialCharacter,
  isTaskStatusFIorLISign,
} from '../../utilities/utilityFunctions';
import FileSelectionView from '../FileSelectionView';
import InspectionFilesView from '../InspectionFilesView';
import useStyles from './InspectionFiles.styles';
interface Props {
  inspectionFilesDataDocument: InspectionFilesType[];
  isEditable: boolean;
}

const InspectionFiles: React.FC<Props> = ({ inspectionFilesDataDocument, isEditable }) => {
  const size = useWindowDimensions();
  const classes = useStyles({ size });
  const isElectrical =
    JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId === 1;
  const online = useOnlineStatus();
  const { errorNotification } = useNotification();
  const [selectedFiles, setSelectedFiles] = useState<AimsFile[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<AimsFile[]>([]);
  const { updateInspectionFile, insertIntoInspectionFileRxdb } = useInspectionFiles();
  const { uploadInspectionFile, uploadFilesInCache } = useFileStorage();
  const [openFileUploadView, setOpenFileUploadView] = useState(false);
  const { setTaskListInfo } = useTaskList();
  const [groupedInspectionFiles, setGrpInspectionFiles] =
    useState<InspectionFilesGroupedBasedOnExtension>({} as InspectionFilesGroupedBasedOnExtension);
  const [arrayImages, setArrayImages] = useState<InspectionFilesGrouped | null>(null);
  const [arrayPdf, setArrayPdf] = useState<InspectionFilesGrouped | null>(null);
  const [arrayVideo, setArrayVideo] = useState<InspectionFilesGrouped | null>(null);
  const [arrayWords, setArrayWords] = useState<InspectionFilesGrouped | null>(null);
  const [arrayExcel, setArrayExcel] = useState<InspectionFilesGrouped | null>(null);
  const [arrayTxt, setArrayTxt] = useState<InspectionFilesGrouped | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inspectionFilesRxdbData: InspectionFilesEdited[] = JSON.parse(
    JSON.stringify(inspectionFilesDataDocument),
  );
  const { CurrentUserSelected } = useMst();
  const { taskData } = useSelectedTask(CurrentUserSelected.taskId);

  const reorder = (list: InspectionFilesEdited[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const move = (
    newlist: InspectionFilesGrouped,
    type: string,
    fileUuidId: string,
    sInd: string,
    dInd: string,
  ) => {
    let destList: InspectionFilesEdited[] = [];
    const list: InspectionFilesGrouped = newlist;
    if (list.hasOwnProperty('false') === false && dInd === 'false' && list[dInd] === undefined) {
      list.false = [];
      destList = list.false;
    }
    if (list.hasOwnProperty('true') === false && dInd === 'true' && list[dInd] === undefined) {
      list.true = [];
      destList = list.true;
    }
    if (list[dInd as keyof InspectionFilesGrouped] !== undefined) {
      destList = list[dInd as keyof InspectionFilesGrouped];
    }

    const sourceList = list[sInd as keyof InspectionFilesGrouped];

    if (
      dInd === 'true' &&
      destList !== undefined &&
      type === 'image/jpeg' &&
      destList?.length >= 2
    ) {
      return list;
    } else {
      const sourceItem = sourceList.find(
        item => item.fileUUID === fileUuidId,
      ) as InspectionFilesEdited;
      sourceItem.showOnReport = !sourceItem.showOnReport;
      destList.push(sourceItem);
      sourceList.splice(sourceList.indexOf(sourceItem), 1);
      return list;
    }
  };

  const handleFileUploadViewClose = () => {
    setSelectedFiles([]);
    setFilesToUpload([]);
    setOpenFileUploadView(false);
  };

  const checkShowOnReportCount = () => {
    const selectedFilesToUpload = groupObjectArrayByKey(selectedFiles, 'showOnReport');
    let countOfShowOnReportImages = 0;
    if (arrayImages?.true?.length) countOfShowOnReportImages += arrayImages?.true?.length;
    if (selectedFilesToUpload?.true?.length) {
      countOfShowOnReportImages += selectedFilesToUpload?.true?.length;
    }
    return countOfShowOnReportImages >= 2;
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (files !== null) {
      for (let i = 0; i < files.length; i++) {
        if (hasSpecialCharacter(files[i].name)) {
          errorNotification(SPECIAL_CHARACTER_ERROR);
          return;
        }
      }
      const listOfFiles: AimsFile[] = Array.from(files as unknown as AimsFile[]);

      listOfFiles.forEach((file, index) => {
        listOfFiles[index].updatedName = autoVersionIfFileExists(file.name, selectedFiles);
        listOfFiles[index].fileDescription = '';
        listOfFiles[index].showOnReport = false;
      });

      setSelectedFiles([...selectedFiles, ...listOfFiles]);
      setOpenFileUploadView(true);
    }
  };

  const onOpenFileUpload = async () => {
    document.getElementById('selectFiles')?.click();
  };

  const fetchFileType = (type: string) => {
    let fileType = type;
    if (type === 'image/png') {
      fileType = 'image/jpeg';
    } else if (type === 'text/csv' || type === 'application/vnd.ms-excel') {
      fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    } else if (type === 'video/mov') {
      fileType = 'video/mp4';
    } else if (
      type === 'application/vnd.openxmlformats-officedocument.wordprocessingm' ||
      type === 'application/msword'
    ) {
      fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    } else {
      fileType = type;
    }
    return fileType;
  };

  const handleImageUploads = async (fileList: AimsFile[]) => {
    setIsLoading(true);
    const files = fileList.map((item: AimsFile) => {
      const fileItem = item;
      fileItem.fileType = fetchFileType(item.type);
      return fileItem;
    });
    const GroupuploadFiles = groupArrayByKey(files, 'fileType');
    for (const i in GroupuploadFiles) {
      GroupuploadFiles[i] = groupArrayByKey(GroupuploadFiles[i], 'showOnReport');
      if (GroupuploadFiles['image/jpeg'] !== undefined) {
        GroupuploadFiles['image/jpeg'].false = GroupuploadFiles['image/jpeg'].false
          ? GroupuploadFiles['image/jpeg'].false
          : [];
      }
      if (
        GroupuploadFiles['image/jpeg'] !== undefined &&
        GroupuploadFiles['image/jpeg'].true !== undefined &&
        GroupuploadFiles['image/jpeg'].true.length > 2
      ) {
        GroupuploadFiles['image/jpeg'].false = GroupuploadFiles['image/jpeg'].false.concat(
          GroupuploadFiles['image/jpeg'].true.slice(2),
        );
        GroupuploadFiles['image/jpeg'].false.filter(
          (item: AimsFile, index: number) =>
            (GroupuploadFiles['image/jpeg'].false[index].showOnReport =
              item.showOnReport === true ? false : item.showOnReport),
        );
        GroupuploadFiles['image/jpeg'].true = GroupuploadFiles['image/jpeg'].true.slice(0, 2);
      }
    }
    const filesToInsert: Promise<InspectionFilesType>[] = [];
    const errorUnSupportedFiles: string[] = [];
    await Promise.all(
      Object.keys(GroupuploadFiles).map((key: string) => {
        Object.keys(GroupuploadFiles[key]).map(item => {
          GroupuploadFiles[key][item].map((file: AimsFile, index: number) => {
            GroupuploadFiles[key][item][index].fileSortOrder =
              groupedInspectionFiles &&
              groupedInspectionFiles.hasOwnProperty(key) &&
              groupedInspectionFiles[
                key as keyof InspectionFilesGroupedBasedOnExtension
              ].hasOwnProperty(item) &&
              groupedInspectionFiles[key as keyof InspectionFilesGroupedBasedOnExtension][
                item as keyof InspectionFilesGrouped
              ].length > 0
                ? groupedInspectionFiles[key as keyof InspectionFilesGroupedBasedOnExtension][
                    item as keyof InspectionFilesGrouped
                  ].length + index
                : index;
            const filePayload: Promise<InspectionFilesType> = uploadInspectionFile(file).catch(
              error => {
                // eslint-disable-next-line no-console
                console.error(
                  '🚀 ~ file: InspectionDetails.tsx ~ line 214 ~ handleImageUploads ~ error',
                  error,
                );
                sentryCaptureException(
                  error,
                  '🚀 ~ file: InspectionFiles.tsx ~ line 219 ~ handleImageUploads ~ error',
                );
                if (!errorUnSupportedFiles?.includes(error?.toString().split(':')[1])) {
                  errorUnSupportedFiles.push(error?.toString().split(':')[1]);
                }
              },
            ) as unknown as Promise<InspectionFilesType>;
            filesToInsert.push(filePayload);
          });
        });
      }),
    )
      .then(async () => {
        const filesToInsertInRxdb: InspectionFilesType[] = (
          await Promise.all(filesToInsert)
        ).filter(item => item);
        if (filesToInsertInRxdb) insertIntoInspectionFileRxdb(filesToInsertInRxdb);
      })
      .catch(error => {
        sentryCaptureException(
          error,
          '🚀 ~ file: InspectionFiles.tsx ~ line 335 ~ handleImageUploads ~ error',
        );
      })
      .finally(async () => {
        if (online) {
          uploadFilesInCache();
        }
        setIsLoading(false);
        handleFileUploadViewClose();
        if (errorUnSupportedFiles?.length) {
          errorNotification(
            `Few files might not be uploaded due to unsupported file format. Unsupported file formats are ${errorUnSupportedFiles?.join()}`,
          );
        }
        if (isTaskStatusFIorLISign(taskData) || CurrentUserSelected.isAdhoc) {
          await setTaskListInfo(TaskStatus.IN_PROGRESS);
        }
      });
  };

  useEffect(() => {
    if (inspectionFilesDataDocument) {
      for (const i in inspectionFilesDataDocument) {
        inspectionFilesRxdbData[i].extension = fetchFileType(
          inspectionFilesDataDocument[i].extension,
        );
      }
    }

    const GroupFiles = groupArrayByKey(inspectionFilesRxdbData, 'extension');

    for (const i in GroupFiles) {
      GroupFiles[i] = groupArrayByKey(GroupFiles[i], 'showOnReport');
    }
    for (const i in GroupFiles) {
      if (i === 'image/jpeg' || GroupFiles['image/jpeg']) {
        setArrayImages(GroupFiles['image/jpeg']);
      } else if (!GroupFiles['image/jpeg']) {
        setArrayImages(null);
      }
      if (i === 'video/mp4' && GroupFiles['video/mp4']) {
        setArrayVideo(GroupFiles['video/mp4']);
      } else if (!GroupFiles['video/mp4']) {
        setArrayVideo(null);
      }
      if (i === 'application/pdf' || GroupFiles['application/pdf']) {
        setArrayPdf(GroupFiles['application/pdf']);
      } else if (!GroupFiles['application/pdf']) {
        setArrayPdf(null);
      }
      if (
        i === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        GroupFiles['application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      ) {
        setArrayWords(
          GroupFiles['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        );
      } else if (
        !GroupFiles['application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      ) {
        setArrayWords(null);
      }
      if (
        i === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        GroupFiles['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
      ) {
        setArrayExcel(
          GroupFiles['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
        );
      } else if (!GroupFiles['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']) {
        setArrayExcel(null);
      }
      if (i === 'text/plain' || GroupFiles['text/plain']) {
        setArrayTxt(GroupFiles['text/plain']);
      } else if (!GroupFiles['text/plain']) {
        setArrayTxt(null);
      }
    }
    setGrpInspectionFiles(GroupFiles);
  }, [inspectionFilesDataDocument]);

  const doSomethingAsync = (arrayItem: InspectionFilesGrouped) => {
    Object.keys(arrayItem).map((key: string) => {
      arrayItem[key as keyof InspectionFilesGrouped].map(
        async (item: InspectionFilesEdited, index: number) => {
          updateInspectionFile(
            item.fileUUID,
            item.name,
            item.showOnReport,
            item.fileDescription ? item.fileDescription : '',
            'False',
            index,
          );
        },
      );
    });
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const draggableIdarray = result.draggableId.split('^');
    const inpsectionFilesData = JSON.parse(JSON.stringify(groupedInspectionFiles));
    const sInd = source.droppableId;
    const dInd = destination.droppableId;

    if (sInd !== dInd) {
      const items = move(
        inpsectionFilesData[draggableIdarray[1]],
        draggableIdarray[1],
        draggableIdarray[0],
        sInd,
        dInd,
      );
      inpsectionFilesData[draggableIdarray[1]] = items;

      setGrpInspectionFiles(inpsectionFilesData);
      doSomethingAsync(inpsectionFilesData[draggableIdarray[1]]);
    } else {
      const reorderResult = reorder(
        inpsectionFilesData[draggableIdarray[1]][sInd],
        source.index,
        destination.index,
      );
      inpsectionFilesData[draggableIdarray[1]][sInd] = reorderResult;

      setGrpInspectionFiles(inpsectionFilesData);
      inpsectionFilesData[draggableIdarray[1]][sInd].map(
        (item: InspectionFilesEdited, index: number) => {
          updateInspectionFile(
            item.fileUUID,
            item.name,
            item.showOnReport,
            item.fileDescription ? item.fileDescription : '',
            'False',
            index,
          );
        },
      );
    }
  };

  return (
    <>
      <Grid className={`${classes.card} ${classes.inspectioFileGrid}`} xs={12} container>
        <Grid item xs={6}>
          <Typography variant="h5" className={classes.titleText}>
            {isElectrical ? 'Preventative Maintenance Files' : 'Inspection Files'}
          </Typography>
        </Grid>

        <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
          <Grid item>
            {isEditable && (
              <Button
                noMinHeight
                color="primary"
                variant="contained"
                onClick={() => {
                  setSelectedFiles([]);
                  onOpenFileUpload();
                }}
                data-testid="inspection-file-add"
              >
                <Icons.PlusFeather />
                <div className={classes.addBtn}>Add...</div>
              </Button>
            )}
            <input
              type="file"
              accept="application/msword,.csv, .xlsx, .xls,.doc,.docx,
                          text/plain, application/pdf, .png, .jpg, .jpeg, .mov, .mp4"
              onChange={evt => onChange(evt)}
              onClick={(event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                const element = event.target as HTMLInputElement;
                element.value = '';
              }}
              multiple
              hidden
              id="selectFiles"
              data-testid="add-inspection-files"
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid xs={12}>
        {isLoading && !isJestTest() ? (
          <CircularProgress color="primary" className={classes.circularProgress} />
        ) : (
          <>
            {inspectionFilesDataDocument.length > 0 ? (
              <>
                <Grid className={classes.filesViewGrid}>
                  <DragDropContext onDragEnd={onDragEnd}>
                    {arrayImages && Object.keys(arrayImages).length > 0 && (
                      <>
                        <p className={classes.headingBorder}>IMAGES</p>

                        <InspectionFilesView
                          arrayList={arrayImages}
                          titleKey={'image/jpeg'}
                          isEditable={isEditable}
                        />
                      </>
                    )}
                  </DragDropContext>
                  {arrayVideo && Object.keys(arrayVideo).length > 0 && (
                    <>
                      <p className={classes.headingBorder}>VIDEOS</p>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <InspectionFilesView
                          arrayList={arrayVideo}
                          titleKey={'video/mp4'}
                          isEditable={isEditable}
                        />
                      </DragDropContext>
                    </>
                  )}
                  {arrayPdf && Object.keys(arrayPdf).length > 0 && (
                    <>
                      <p className={classes.headingBorder}>PDF</p>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <InspectionFilesView
                          arrayList={arrayPdf}
                          titleKey={'application/pdf'}
                          isEditable={isEditable}
                        />
                      </DragDropContext>
                    </>
                  )}
                  {arrayWords && Object.keys(arrayWords).length > 0 && (
                    <>
                      <p className={classes.headingBorder}>WORDS</p>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <InspectionFilesView
                          arrayList={arrayWords}
                          titleKey={
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                          }
                          isEditable={isEditable}
                        />
                      </DragDropContext>
                    </>
                  )}

                  {arrayExcel && Object.keys(arrayExcel).length > 0 && (
                    <>
                      <p className={classes.headingBorder}>EXCEL</p>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <InspectionFilesView
                          arrayList={arrayExcel}
                          titleKey={
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                          }
                          isEditable={isEditable}
                        />
                      </DragDropContext>
                    </>
                  )}
                  {arrayTxt && Object.keys(arrayTxt).length > 0 && (
                    <>
                      <p className={classes.headingBorder}>TEXT</p>

                      <DragDropContext onDragEnd={onDragEnd}>
                        <InspectionFilesView
                          arrayList={arrayTxt}
                          titleKey={TEXT}
                          isEditable={isEditable}
                        />
                      </DragDropContext>
                    </>
                  )}
                </Grid>
              </>
            ) : (
              <Typography className={classes.fileMessage}>
                {!isEditable
                  ? 'No file added for this inspection.'
                  : 'Files are not yet added for this inspection. Add files to inspection.'}
              </Typography>
            )}
          </>
        )}
      </Grid>
      {openFileUploadView && (
        <FileSelectionView
          selectedFilesProps={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          setFilesToUpload={setFilesToUpload}
          filesToUpload={filesToUpload}
          onOpenFileUpload={onOpenFileUpload}
          openFileUploadView={openFileUploadView}
          handleFileUploadViewClose={handleFileUploadViewClose}
          checkShowOnReportCount={checkShowOnReportCount}
          isDirectFileUpload
          handleDirectFileUpload={(files: AimsFile[]) => handleImageUploads(files)}
          isLoading={isLoading}
          isEditable={isEditable}
        />
      )}
    </>
  );
};

export default observer(InspectionFiles);
